'use client';

/**
 * ReportTile — shared visual primitive for mode9's explorer
 * report-picker cards. Each report = one card; each card delegates
 * to this tile.
 *
 * Renders a clickable row (icon + label + optional description) plus
 * a kebab affordance opening a small action menu. Clicking the row
 * dispatches `setExplorerSelection(selection)`; clicking the kebab
 * opens the menu (the only action today is Download JSON, which
 * exports the response shape the report's BFF strategy ships).
 *
 * Typography from `tokens.ts` (the current lane — NOT the deprecated
 * TypographyBundle): the report name is `text.title` (the em-relative
 * "name" role — a step under a card heading, right for tiles stacked in a
 * narrow pane), the one-line description `text.muted`, menu items
 * `text.body`. The name is neutral by default; `text-primary` is applied
 * locally on the active state so inactive tiles read as plain title
 * weight. Migrated off the bundle so every report-picker (System Obeya's
 * three + analytics coverage) renders from one token vocabulary — no
 * per-@card `typography=` needed. Container chrome (padding,
 * hover, active border, icon chip, menu styling) is inline as a
 * render concern per the mode7 doctrine. Menu actions are passed in
 * by the calling card; action labels are pulled from the labels dict.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py — the
 *      explorer report-picker @card decorations
 */

import { useState } from 'react';
import { MoreVertical, Download, ClipboardCopy, type LucideIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useAffordance, useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import {
  copyAllCardsAsMarkdown,
  downloadCapture,
  getCardExports,
  useCardExport,
  waitForCanvasMutation,
} from '@/lib/design/card-export';

interface ReportTileProps {
  selection: string;
  label: string;
  description?: string;
  Icon: LucideIcon;
  /** Label for the "Copy report" menu item (from labels dict). */
  copyReportLabel?: string;
  /** Label for the "Download capture" menu item (from labels dict).
   *  Capture = one file containing the raw BFF payload(s) AND the
   *  rendered scene mirror, stapled at the moment of capture so the
   *  pair is correspondent by construction. */
  downloadCaptureLabel?: string;
  /** Aria label for the kebab button (from labels dict). */
  menuLabel?: string;
  /** Mode key for the capture envelope. Owner card supplies this
   *  since the rail/explorer don't carry mode locally. */
  mode?: string;
  /** Backend @card content_key — for the scene mirror. */
  contentKey?: string;
}

export function ReportTile({
  selection,
  label,
  description,
  Icon,
  copyReportLabel,
  downloadCaptureLabel,
  menuLabel,
  mode,
  contentKey,
}: ReportTileProps) {
  const queryClient = useQueryClient();
  const currentSelection = useLayoutStore((s) => s.explorerSelection);
  // `default_selection` is the matrix-declared answer to "what
  // selection does the canvas render when none is explicitly
  // picked?" — surfaces directly on the wire (TotalModeConfig).
  // Used to populate the capture envelope's `resolved_selection`
  // metadata without any mode-specific archaeology.
  const defaultSelection = useTotalMatrixStore(
    (s) => (mode ? s.matrix?.[mode]?.default_selection ?? null : null),
  );
  const active = currentSelection !== null
    ? currentSelection === selection
    : selection === defaultSelection;
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const lang = useLanguageStore((s) => s.lang);
  // Role-gated menu actions — the matrix ships only the affordances
  // this actor's role may see (`AffordanceSpec.visible_to`, filtered
  // server-side at the wire projection). `undefined` = not in this
  // actor's projection = don't render the action. The resolved value
  // doubles as the data-testid per the affordance doctrine. UI hiding
  // is a courtesy; the capture_scene strategy enforces independently
  // at the auth boundary.
  const copyReportTestId = useAffordance('explorer.report_tile.copy_report');
  const downloadCaptureTestId = useAffordance('explorer.report_tile.download_capture');

  // Scene-snapshot registration — explorer report-pickers are
  // navigation, but "what's available" is part of "what's on
  // screen." Without these, a snapshot can't tell which other
  // reports were one click away.
  //
  // `active` is state, not data — kept only on `state` to avoid
  // duplicating it on `data`. The visible data is the navigation
  // target identity + label + description.
  // No query_key: the explorer report-tile is a navigation
  // primitive — its CONTENT (selection_target + label + description)
  // is identity, not data. The underlying useSystemObeyaView fetch
  // happens to power labels (incidental), but the tile itself isn't
  // consuming that query AS CONTENT. Per the doctrine: query_key
  // declares "what data feeds this card's content"; static and
  // navigation cards report none, so cached payloads they don't
  // actually consume don't leak into the envelope.
  useCardExport({
    label,
    text: `${label}${description ? ` — ${description}` : ''}`,
    json: {
      selection_target: selection,
      label,
      description: description ?? null,
    },
    content_key: contentKey,
    surface: 'explorer',
    state: { active },
    status: 'loaded',
    query_key: null,
  });

  const [menuOpen, setMenuOpen] = useState(false);

  const handleDownloadCapture = async () => {
    let explicitSelection: string | null;
    let resolvedSelection: string | null;
    let source: 'explicit' | 'default' | 'unknown';

    if (!active) {
      // Kebab on a non-active tile: behave like a row click + then
      // a kebab on the now-active tile. The user's intent ("give me
      // a capture for THIS report") collapses to one action. We:
      //   1. Snapshot the canvas content_keys before navigation.
      //   2. Set the explorer selection — the matrix lookup re-
      //      renders the canvas with the new preset's cards.
      //   3. Wait for the canvas registry to mutate (new cards
      //      mount, status flips to 'loaded' as data lands) using
      //      the same registry the capture itself reads.
      //   4. Then capture.
      const prevKeys = new Set(
        getCardExports()
          .filter((e) => e.surface === 'canvas')
          .map((e) => e.content_key)
          .filter((k): k is string => !!k),
      );
      setExplorerSelection(selection);
      await waitForCanvasMutation(prevKeys, 5000);
      // After navigation, this tile's `selection` IS the explicit
      // pick — the layout-store has updated, the canvas is painting
      // this report's cards.
      explicitSelection = selection;
      resolvedSelection = selection;
      source = 'explicit';
    } else {
      // Tile already active — capture current state. resolved_selection
      // falls back to the matrix's default_selection when the user
      // hasn't made an explicit pick (the default-fallback case).
      explicitSelection = currentSelection;
      resolvedSelection = currentSelection ?? defaultSelection;
      source =
        currentSelection !== null
          ? 'explicit'
          : (resolvedSelection !== null ? 'default' : 'unknown');
    }

    // The capture is now strategy-routed — `downloadCapture` POSTs the
    // observed envelope to `capture_scene`, the BFF notarises +
    // attests + audits, and we receive the wrapped envelope as
    // content_base64 for the standard download path. Filename is
    // server-authored (server can't trust client wall-clock for
    // audit-correlated filenames). The `_baseName` arg is kept for
    // backward compat but ignored by the new implementation.
    try {
      await downloadCapture(
        queryClient,
        {
          mode: mode ?? null,
          selection: explicitSelection,
          resolved_selection: resolvedSelection,
          selection_source: source,
          viewer_lang: lang ?? 'en',
        },
      );
    } catch (err) {
      // Strategy-only — no fallback. The error itself is diagnostic;
      // log to console for now and surface to the user via the
      // standard toast pattern when this card adopts toast in a
      // follow-up. "BFF unreachable at 14:32" is more useful than a
      // silently-degraded offline envelope.
      console.error('[ReportTile] capture failed:', err);
    }
  };

  return (
    <div
      className={cn(
        'relative w-full flex items-start gap-2 rounded-md transition-colors',
        active
          ? 'bg-primary/10 border border-primary/30'
          : 'hover:bg-sidebar-accent border border-transparent',
      )}
    >
      {/* Row click target — selection */}
      <button
        type="button"
        onClick={() => setExplorerSelection(selection)}
        className="flex-1 flex items-start gap-3 px-3 py-2.5 text-left min-w-0"
      >
        <div
          className={cn(
            'p-2 rounded-md shrink-0',
            active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn(text.title, active && 'text-primary')}>{label}</div>
          {description && (
            <div className={cn(text.muted, 'mt-0.5 truncate')}>{description}</div>
          )}
        </div>
      </button>

      {/* Kebab — opens the per-report actions menu (always present
          on every report tile; capture is universally available
          and copy is independent of any per-report state). */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label={menuLabel ?? ''}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1.5 mr-1 mt-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>

      {menuOpen && (
        <>
          {/* Click-outside backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Menu — Copy report (markdown clipboard) + Download
              capture (one file: raw payloads + rendered scene). The
              earlier 3-item version (Copy / Download JSON / Download
              scene) is collapsed because the capture envelope
              contains BOTH the BFF payload(s) and the scene mirror —
              two separate downloads no longer make sense. Anyone
              who wants only one section extracts it. */}
          <div
            role="menu"
            className="absolute right-1 top-9 z-50 w-52 rounded-md border border-border bg-popover shadow-md py-1"
          >
            {/* Each item renders only when its affordance is in this
                actor's role-filtered projection — `undefined` means
                the matrix dropped it (visible_to gating) and the
                action doesn't exist for this user. */}
            {copyReportTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={copyReportTestId}
                onClick={() => {
                  setMenuOpen(false);
                  void copyAllCardsAsMarkdown();
                }}
                className={cn(
                  text.body,
                  'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left',
                )}
              >
                <ClipboardCopy className="w-3.5 h-3.5" />
                {copyReportLabel ?? ''}
              </button>
            )}
            {downloadCaptureTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={downloadCaptureTestId}
                onClick={() => {
                  setMenuOpen(false);
                  // Fire-and-forget the async handler — navigation +
                  // wait + download are user-observable; React Query /
                  // Zustand cleanup happens on its own.
                  void handleDownloadCapture();
                }}
                className={cn(
                  text.body,
                  'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left',
                )}
              >
                <Download className="w-3.5 h-3.5" />
                {downloadCaptureLabel ?? ''}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

